export const gramsToPounds = (grams: number | null | undefined): number | null =>
  grams == null ? null : grams / 453.59237;

export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null) return "--";
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))} sec`;
  const minutes = Math.round(seconds / 60);
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

type NumericMetric = number | string | null | undefined;

const finiteMetricNumber = (value: NumericMetric): number | null => {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const percentChange = (current: NumericMetric = 0, previous: NumericMetric = 0): number | null => {
  const currentValue = finiteMetricNumber(current);
  const previousValue = finiteMetricNumber(previous);
  if (currentValue == null || previousValue == null || previousValue === 0) return null;
  const change = ((currentValue - previousValue) / previousValue) * 100;
  return Number.isFinite(change) ? change : null;
};

export const changeText = (current?: NumericMetric, previous?: NumericMetric): string => {
  const change = percentChange(current, previous);
  return change == null ? "No prior baseline" : `${change >= 0 ? "+" : ""}${change.toFixed(1)}% vs prior`;
};

export const wifiStrengthPercent = (rssi: NumericMetric): number | null => {
  const value = finiteMetricNumber(rssi);
  if (value == null) return null;
  return Math.max(0, Math.min(100, Math.round((value + 100) * 2)));
};

export const hasFullTrendWindow = (
  earliestObservedAt: string | null | undefined,
  latestObservedAt: string | null | undefined,
  days: number
): boolean => {
  if (!earliestObservedAt || !latestObservedAt || days < 1) return false;
  const earliest = new Date(earliestObservedAt).getTime();
  const latest = new Date(latestObservedAt).getTime();
  if (!Number.isFinite(earliest) || !Number.isFinite(latest)) return false;
  return latest - earliest >= (days - 1) * 86400000;
};

export const dateKeyInTimeZone = (value: Date | string | number, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
};

export type TodayDailyMetric = {
  feeding_count?: NumericMetric;
  feeding_seconds?: NumericMetric;
  very_close_seconds?: NumericMetric;
  banned_overrides?: NumericMetric;
};

export type TodayFeedSession = {
  started_at: string;
  ended_at: string | null;
};

export type TodaySummary = {
  visits: number;
  accessSeconds: number;
  closeSeconds: number;
  overrides: number;
  activeSessions: number;
};

const nonnegativeMetric = (value: NumericMetric): number => Math.max(0, finiteMetricNumber(value) || 0);

export const buildTodaySummary = (
  daily: TodayDailyMetric | null | undefined,
  sessions: TodayFeedSession[],
  timeZone: string,
  now = new Date()
): TodaySummary => {
  const todayKey = dateKeyInTimeZone(now, timeZone);
  const nowMs = now.getTime();
  const activeSessions = sessions.filter((session) =>
    !session.ended_at && dateKeyInTimeZone(session.started_at, timeZone) === todayKey
  );
  const activeAccessSeconds = activeSessions.reduce((total, session) => {
    const startedAt = new Date(session.started_at).getTime();
    return total + (Number.isFinite(startedAt) ? Math.max(0, (nowMs - startedAt) / 1000) : 0);
  }, 0);

  return {
    visits: nonnegativeMetric(daily?.feeding_count) + activeSessions.length,
    accessSeconds: nonnegativeMetric(daily?.feeding_seconds) + activeAccessSeconds,
    closeSeconds: nonnegativeMetric(daily?.very_close_seconds),
    overrides: nonnegativeMetric(daily?.banned_overrides),
    activeSessions: activeSessions.length
  };
};

export type WeightObservation = {
  observed_at: string;
  weight_grams: number | string;
};

export const buildRecentWeightCluster = <T extends WeightObservation>(
  points: T[],
  windowMs = 2 * 60 * 60 * 1000,
  maxPoints = 12
): T[] => {
  const valid = points
    .filter((point) => Number.isFinite(new Date(point.observed_at).getTime()) && finiteMetricNumber(point.weight_grams) != null)
    .sort((left, right) => new Date(left.observed_at).getTime() - new Date(right.observed_at).getTime());
  if (valid.length < 2 || windowMs <= 0 || maxPoints < 2) return [];
  const latestAt = new Date(valid[valid.length - 1].observed_at).getTime();
  const cluster = valid.filter((point) => latestAt - new Date(point.observed_at).getTime() <= windowMs).slice(-maxPoints);
  return cluster.length >= 2 ? cluster : [];
};

export type FeedingTimeProfileRow = {
  hour_of_day: number | string;
  visit_count: number | string;
  close_seconds: number | string;
};

export type FeedingTimeBucket = {
  hour: number;
  label: string;
  shortLabel: string;
  visits: number;
  closeSeconds: number;
};

const hourLabel = (hour: number, short = false): string => {
  const normalized = hour % 12 || 12;
  const suffix = hour < 12 ? (short ? "a" : "AM") : (short ? "p" : "PM");
  return `${normalized} ${suffix}`;
};

export const buildFeedingTimeBuckets = (rows: FeedingTimeProfileRow[]): FeedingTimeBucket[] => {
  const buckets = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    label: hourLabel(hour),
    shortLabel: hourLabel(hour, true),
    visits: 0,
    closeSeconds: 0
  }));

  for (const row of rows) {
    const hour = Number(row.hour_of_day);
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) continue;
    buckets[hour].visits = Math.max(0, Number(row.visit_count) || 0);
    buckets[hour].closeSeconds = Math.max(0, Number(row.close_seconds) || 0);
  }

  return buckets;
};
